import { Helmet } from '@/lib/helmet-shim';

interface PerformanceTagsProps {
  /** Critical external resources to preconnect */
  preconnectUrls?: string[];
  /** Resources to prefetch for likely next navigation */
  prefetchUrls?: string[];
  /** Critical resources to preload */
  preloadResources?: Array<{
    href: string;
    as: 'script' | 'style' | 'image' | 'font' | 'fetch';
    type?: string;
    crossOrigin?: 'anonymous' | 'use-credentials';
  }>;
  /** Likely next pages to prerender */
  prerenderUrls?: string[];
}

/**
 * PerformanceTags Component
 * 
 * Implements performance-focused meta tags for faster page loads:
 * - Preconnect: Establish early connections to critical origins
 * - DNS Prefetch: Resolve DNS for anticipated resources
 * - Prefetch: Load resources for likely next navigations
 * - Preload: Load critical resources with high priority
 * - Prerender: Speculatively render likely next pages
 * 
 * @see https://web.dev/preconnect-and-dns-prefetch/
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types/preload
 */
export const PerformanceTags = ({
  preconnectUrls = [],
  prefetchUrls = [],
  preloadResources = [],
}: PerformanceTagsProps) => {
  // Skip if nothing custom to add (defaults are already in index.html)
  if (!preconnectUrls.length && !prefetchUrls.length && !preloadResources.length) {
    return null;
  }

  return (
    <Helmet>
      {preconnectUrls.map((url) => (
        <link key={`preconnect-${url}`} rel="preconnect" href={url} crossOrigin="anonymous" />
      ))}
      {prefetchUrls.map((url) => (
        <link key={`prefetch-${url}`} rel="prefetch" href={url} as="document" />
      ))}
      {preloadResources.map((resource) => (
        <link
          key={`preload-${resource.href}`}
          rel="preload"
          href={resource.href}
          as={resource.as}
          type={resource.type}
          crossOrigin={resource.crossOrigin}
        />
      ))}
    </Helmet>
  );
};

export default PerformanceTags;
